import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material'
import {
  accountService,
  driverService,
  vehicleService,
  AccountDto,
  DriverDto,
  UserDto,
  VehicleDto,
} from '../api/adminServices'
import { mitigationService, ManualPlacementRequest } from '../api/mitigationService'
import { useToast } from './useToast'

interface ManualPlacementDialogProps {
  shipmentRequestId: string
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function ManualPlacementDialog({
  shipmentRequestId,
  open,
  onClose,
  onSuccess,
}: ManualPlacementDialogProps) {
  const toast = useToast()

  const [freighterAccount, setFreighterAccount] = useState<AccountDto | null>(null)
  const [vehicle, setVehicle] = useState<VehicleDto | null>(null)
  const [driver, setDriver] = useState<DriverDto | null>(null)
  const [foreman, setForeman] = useState<UserDto | null>(null)
  const [carrierPayout, setCarrierPayout] = useState('')
  const [shipperPrice, setShipperPrice] = useState('')
  const [carrierName, setCarrierName] = useState('')
  const [note, setNote] = useState('')

  const { data: freighters, isLoading: loadingFreighters } = useQuery({
    queryKey: ['accounts-freighters'],
    queryFn: async () => {
      const page = await accountService.getAll(0, 500)
      const content: AccountDto[] = page?.content || page || []
      return content.filter((a) => a.type === 'FREIGHTER')
    },
    enabled: open,
  })

  const freighterAccountId = freighterAccount?.id

  const { data: vehicles, isLoading: loadingVehicles } = useQuery({
    queryKey: ['vehicles-by-account', freighterAccountId],
    queryFn: () => vehicleService.getAllByAccount(freighterAccountId as string),
    enabled: open && !!freighterAccountId,
  })

  const { data: drivers, isLoading: loadingDrivers } = useQuery({
    queryKey: ['drivers-by-account', freighterAccountId],
    queryFn: () => driverService.getAllByAccount(freighterAccountId as string),
    enabled: open && !!freighterAccountId,
  })

  const { data: foremen, isLoading: loadingForemen } = useQuery({
    queryKey: ['foremen-by-account', freighterAccountId],
    queryFn: async () => {
      const page = await accountService.getUsersByAccount(freighterAccountId as string, 0, 100)
      const users: UserDto[] = page?.content || page || []
      return users.filter((u) => u.roles?.includes('FREIGHTER_FOREMAN'))
    },
    enabled: open && !!freighterAccountId,
  })

  useEffect(() => {
    if (!open) {
      setFreighterAccount(null)
      setVehicle(null)
      setDriver(null)
      setForeman(null)
      setCarrierPayout('')
      setShipperPrice('')
      setCarrierName('')
      setNote('')
    }
  }, [open])

  useEffect(() => {
    setVehicle(null)
    setDriver(null)
    setForeman(null)
  }, [freighterAccountId])

  const placementMutation = useMutation({
    mutationFn: (req: ManualPlacementRequest) =>
      mitigationService.manualPlacement(shipmentRequestId, req),
    onSuccess: () => {
      toast.success('Carrier placed — Job created through normal acceptance path')
      onSuccess()
    },
    onError: (e) => toast.errorFrom(e, 'Manual placement failed'),
  })

  const canSubmit =
    freighterAccount &&
    vehicle &&
    driver &&
    foreman &&
    carrierPayout &&
    parseFloat(carrierPayout) > 0

  const handleSubmit = () => {
    if (!canSubmit) return
    const request: ManualPlacementRequest = {
      freighterAccountId: freighterAccount!.id,
      vehicleId: vehicle!.id,
      driverId: driver!.id,
      foremanUserId: foreman!.id,
      carrierPayout: parseFloat(carrierPayout),
      shipperPrice: shipperPrice ? parseFloat(shipperPrice) : undefined,
      carrierName: carrierName.trim() || undefined,
      note: note.trim() || undefined,
    }
    placementMutation.mutate(request)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manual placement</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          The carrier account, vehicle, driver and foreman must already exist in the system.
          Placement runs through the same acceptance path as a foreman tap — tracking, POD and
          billing all follow the normal flow.
        </Alert>

        <Stack spacing={2}>
          <Autocomplete
            options={freighters || []}
            loading={loadingFreighters}
            value={freighterAccount}
            onChange={(_, v) => setFreighterAccount(v)}
            getOptionLabel={(a) => a.businessName || a.name || a.id}
            renderInput={(params) => (
              <TextField {...params} label="Freighter account" required />
            )}
          />

          {freighterAccountId && (
            <>
              <Autocomplete
                options={vehicles || []}
                loading={loadingVehicles}
                value={vehicle}
                onChange={(_, v) => setVehicle(v)}
                getOptionLabel={(v) =>
                  [v.plateNo, v.maker, v.model].filter(Boolean).join(' · ') || v.id
                }
                renderInput={(params) => (
                  <TextField {...params} label="Vehicle" required />
                )}
              />

              <Autocomplete
                options={drivers || []}
                loading={loadingDrivers}
                value={driver}
                onChange={(_, v) => setDriver(v)}
                getOptionLabel={(d) => d.user?.name || d.licenseNo || d.id}
                renderInput={(params) => (
                  <TextField {...params} label="Driver" required />
                )}
              />

              <Autocomplete
                options={foremen || []}
                loading={loadingForemen}
                value={foreman}
                onChange={(_, v) => setForeman(v)}
                getOptionLabel={(u) => u.name || u.email || u.id}
                renderInput={(params) => (
                  <TextField {...params} label="Foreman" required />
                )}
              />
            </>
          )}

          <TextField
            label="Carrier payout (ILS)"
            type="number"
            required
            value={carrierPayout}
            onChange={(e) => setCarrierPayout(e.target.value)}
            helperText="What the desk agreed with the carrier on the phone"
            inputProps={{ min: 0, step: 1 }}
          />

          <TextField
            label="Shipper price override (optional)"
            type="number"
            value={shipperPrice}
            onChange={(e) => setShipperPrice(e.target.value)}
            helperText="Leave empty unless the shipper agreed to a different price on the phone"
            inputProps={{ min: 0, step: 1 }}
          />

          <TextField
            label="Carrier name (for notification)"
            value={carrierName}
            onChange={(e) => setCarrierName(e.target.value)}
          />

          <TextField
            label="Internal note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            rows={2}
          />
        </Stack>

        {placementMutation.isPending && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={placementMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || placementMutation.isPending}
        >
          Place carrier
        </Button>
      </DialogActions>
    </Dialog>
  )
}
